import { withTheme } from '@emotion/react';
import classNames from 'classnames';
import React from 'react';
import compose from 'utils/compose';

import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';

import ArrowIcon from '@mui/icons-material/ChevronRightRounded';
import Divider from '@mui/material/Divider/index';
import IconButton from '@mui/material/IconButton/index';
import ListItem from '@mui/material/ListItem/index';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Popper from '@mui/material/Popper/index';
import Typography from '@mui/material/Typography';

import { ensureArray } from 'app-components/utils/core/immutableHelpers';

import ListItemIcon from '@mui/material/ListItemIcon';
import {
  getStandardDrawerFloatingSx,
  getStandardDrawerPaper,
  useStandardDrawerLayerContainer,
} from 'components/Drawers/StandardDrawer';
import QTip from 'components/QuickenControls/QTip';
import { noop } from 'lodash';

const DRAWER_MENU_GUTTER = 8;
const DRAWER_MENU_COLUMN_COUNT = 3;

export const getDrawerMenuLayout = ({ columnCount, drawerWidth }) => {
  const availableWidth = Math.max(drawerWidth - DRAWER_MENU_GUTTER * 2, 0);
  const resolvedColumnCount = Math.max(columnCount, 1);
  const columnWidth = availableWidth / DRAWER_MENU_COLUMN_COUNT;
  const visibleColumnCount = Math.min(resolvedColumnCount, DRAWER_MENU_COLUMN_COUNT);
  const width = columnWidth * visibleColumnCount;

  return {
    columnWidth,
    width,
  };
};

/*
 *   - 'options' for the menu should be passed down as an array of objects, like the following
 *   {
 *     label: <string>,            // this is what's displayed in the menu
 *     value: <any>,               // this is what's returned when an option is selected
 *
 *     ***-OPTIONAL PROPERTIES-***
 *     subMenu: <array of options> // will render a subMenu under this list item
 *     id: <string>                // will give the menu option a specified id
 *     customRender: <node>        // will be displayed inside the list item instead of regular Typography wrapping
 *     isSubheader: <bool>         // if true shows the option as a subheader and restricts selecting it
 *     onSelect: <func>            // overrides regular onSelect functionality (which regularly returns 'value')
 *     divider: <bool>             // adds a list divider after this option
 *     nop: <bool>                 // nop === no-operation, this option will not display in the menu
 *     backgroundColor: <string>   // overrides list item bg color, separate from 'style' to ensure hover colors display properly
 *     style: <object>             // overrides styling on the list item, to override styling on the text use 'customRender'
 *                                     note: do NOT add backgroundColor in here, it will mess up the hover effects
 *     startIcon:                  // icon that goes to left of option text
 *     endIcon:                   // icon that goes to right of option text
 *     subtext:                    // text displayed under the main option
 *     tooltip: <string>           // If you wish to show a tooltip when hovering over the item
 *     saveMenuSelectionOnTab: <bool>           // Performs a "save" (onChange) when tabbing
 *
 *   }
 *
 *   - currently doesn't support groupLists as options
 *   - keyDown events are caught by the menu for navigation
 *
 */

const styles = (theme) => ({
  divider: {
    margin: `${theme.shape.padding[1]}px 0px`,
  },
  item: {
    cursor: 'pointer',
    justifyContent: 'space-between',
    borderRadius: '8px',
    ':not(.keyboard-mode) &': {
      '&:hover': {
        backgroundColor: `${theme.palette.action.hover} !important`,
      },
    },
  },
  disabledItem: {
    color: theme.palette.text.disabled,
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius * 1,
  },
  popper: {
    zIndex: theme.zIndex.tooltip,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[16],
    maxHeight: '95%',
  },
  primaryMenu: {
    maxHeight: '100%',
    overflowY: 'auto',
    padding: theme.shape.padding[2],
  },
  staticMenu: {
    maxHeight: 'none',
    overflowY: 'visible',
  },
  root: {
    zIndex: theme.zIndex.tooltip,
  },
  rootPopper: {
    minWidth: theme.spacing(33),
    maxWidth: '50%',
  },
  drawerMenuColumns: {
    alignItems: 'stretch',
    display: 'flex',
    maxHeight: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  drawerMenuColumn: {
    boxSizing: 'border-box',
    flex: '0 0 auto',
    minWidth: 0,
    '& + &': {
      borderLeft: `1px solid ${theme.palette.border.main}`,
    },
  },
  secondaryMenu: {
    maxHeight: 'calc(95vh)',
    maxWidth: 280,
    overflowY: 'auto',
    borderRadius: theme.shape.borderRadius * 2,
    overflowX: 'hidden',
    padding: theme.shape.padding[2],
  },
  subheader: {
    color: `${theme.palette.text.secondary}`,
    textAlign: 'left',
    padding: theme.shape.padding[2],
  },
  startIcon: {
    alignItems: 'center',
    minWidth: 30,
    marginRight: theme.spacing(1),
  },
  iconColor: {
    color: theme.palette.icon.main,
  },
  iconColorDisabled: {
    color: theme.palette.text.disabled,
  },
  rightArrowStyles: {
    padding: 0,
    width: 20,
    height: 20,
  },
  rightArrowStylesDisabled: {
    padding: 0,
    width: 20,
    height: 20,
    color: theme.palette.text.disabled,
  },
  rightArrowButton: {
    padding: 0,
    width: 30,
    height: 30,
  },
  labelStyles: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    marginLeft: 5,
    paddingTop: 5,
    paddingBottom: 5,
  },
});

class NestedQMenu extends React.PureComponent {
  constructor(props) {
    super(props);
    this.scrollRefInternal = [React.createRef()];
    this.trackedOptions = [];
    this.state = {
      // the lists match up indecision to the depth of the menu
      path: [],
      subNodes: [],
      interactionMode: 'keyboard',
      headerHeight: 0,
      drawerWidth: 0,
      firstMenuHeight: 0,
    };
    this.composeItems(props.options, 0);
  }

  componentDidMount() {
    this.observeDrawerPaper();
    this.initMenu('first');
    this.updateFirstMenuHeight();
    if (this.props.open) {
      document.addEventListener('keydown', this.handleKeyPress, { capture: true });
      document.addEventListener('mousemove', this.handleMouseMove);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.anchorEl !== this.props.anchorEl ||
      prevProps.useDrawerColumnLayout !== this.props.useDrawerColumnLayout
    ) {
      this.observeDrawerPaper();
    }

    if (prevProps.options !== this.props.options) {
      this.initMenu('first');
    }

    if (!prevProps.open && this.props.open) {
      document.addEventListener('keydown', this.handleKeyPress, { capture: true });
      document.addEventListener('mousemove', this.handleMouseMove);
    } else if (prevProps.open && !this.props.open) {
      document.removeEventListener('keydown', this.handleKeyPress, { capture: true });
      document.removeEventListener('mousemove', this.handleMouseMove);
      this.clearAllTracking();
    }

    if (prevState.subNodes.length !== this.state.subNodes.length && this.drawerMenuColumns) {
      this.drawerMenuColumns.scrollLeft = this.drawerMenuColumns.scrollWidth;
    }

    this.updateFirstMenuHeight();
  }

  componentWillUnmount() {
    this.stopObservingDrawerPaper();
    document.removeEventListener('keydown', this.handleKeyPress, { capture: true });
    document.removeEventListener('mousemove', this.handleMouseMove);
    this.closeMenus();
  }

  getDrawerPaper = () => (this.props.useDrawerColumnLayout ? getStandardDrawerPaper(this.props.anchorEl) : null);

  updateDrawerWidth = () => {
    const drawerWidth = this.observedDrawerPaper?.clientWidth ?? 0;
    if (drawerWidth !== this.state.drawerWidth) {
      this.setState({ drawerWidth });
    }
  };

  stopObservingDrawerPaper = () => {
    this.drawerResizeObserver?.disconnect();
    this.drawerResizeObserver = null;
    window.removeEventListener('resize', this.updateDrawerWidth);
    this.observedDrawerPaper = null;
  };

  observeDrawerPaper = () => {
    const drawerPaper = this.getDrawerPaper();
    if (drawerPaper === this.observedDrawerPaper) return;

    this.stopObservingDrawerPaper();
    this.observedDrawerPaper = drawerPaper;
    this.updateDrawerWidth();

    if (!drawerPaper) return;

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', this.updateDrawerWidth);
      return;
    }

    this.drawerResizeObserver = new ResizeObserver(this.updateDrawerWidth);
    this.drawerResizeObserver.observe(drawerPaper);
  };

  scrollRef = () => this.scrollRefInternal[this.state.path.length];

  handleMouseMove = () => {
    if (this.state.interactionMode !== 'mouse') {
      this.setState({ interactionMode: 'mouse' });
    }
  };

  openSubMenu = (option, selectFirst = false) => {
    let { path, subNodes } = this.state;
    const { menuDepth } = option;

    // update the path to the currently hovered item
    path = path.slice(0, menuDepth).concat([option]);

    // trim subnodes to existing depth
    subNodes = subNodes.slice(0, menuDepth);
    // add subnodes for current layer
    subNodes = option.subMenu ? subNodes.concat([ensureArray(option.subMenu)]) : subNodes;

    if (selectFirst) {
      // auto-highlights the first available option in submenu
      const nextDepth = path.length;
      const nextIndex = 0;
      const nextOption = ensureArray(option.subMenu)[nextIndex];
      if (nextOption) {
        const optionToSelect = {
          ...nextOption,
          id: this.getOptionId(nextOption, nextDepth, nextIndex),
          menuDepth: nextDepth,
          menuIndex: nextIndex,
        };
        path = path.concat([optionToSelect]);
      }
    }

    this.setState({ path, subNodes });
  };

  handleOptionHover = (_event, option) => {
    let { interactionMode } = this.state;

    // ignore hover events if in keyboard mode
    if (interactionMode === 'keyboard') return;

    this.openSubMenu(option);
  };

  doOptionSelect = (event, option) => {
    event.stopPropagation();
    if (option.onSelect) {
      option.onSelect(event);
      this.openSubMenu(option);
      return false;
    } else {
      if (this.props.onChange && option.value) {
        this.props.onChange(option.value, { reason: event.type === 'click' ? 'mouse' : 'keyboard' });
      }
      if (this.props.currentHover) {
        this.props.currentHover(null);
      }
      return true;
    }
  };

  handleOptionClick = (event, option) => {
    this.setState({ interactionMode: 'mouse' });

    // this is really meant for testing - unit & cypress; otherwise, handleOptionHover already ran
    const currentOption = this.state.path[this.getCurrentDepth()];
    if (!currentOption || option?.id !== currentOption?.id) {
      this.handleOptionHover(event, option);
    }

    this.doOptionSelect(event, option);
  };

  // sends a 'fake' event to key press because logic is already there
  handleArrowClick = (event) => {
    this.handleKeyPress({
      // key: 'ArrowRight',
      keyCode: 39,
      stopPropagation: () => 0,
      preventDefault: () => 0,
    });
    event.stopPropagation();
  };

  /**
   * Handles key press events for keyboard navigation.
   * Predicts submenu id, avoids accessing trackedOptions during prediction,
   * and sets path/subNodes only as needed.
   */
  handleKeyPress = (event) => {
    // If menu is closed or has no options, ignore event
    if (this.props.options.length === 0 || !this.props.open) {
      return;
    }

    let { path, subNodes } = this.state;
    let currentDepth = this.getCurrentDepth();

    if (this.state.interactionMode !== 'keyboard') {
      // When we switch to keyboard mode and there are more subNodes displayed vs selected path (which can
      // happen if mouse was on parent-node, child submenu was displayed, and then we move to keyboard mode),
      // then remove displayed but not yet focused submenu.
      //
      subNodes = subNodes.slice(0, currentDepth);
      this.setState({
        interactionMode: 'keyboard',
        subNodes,
      });
    }

    const currentOption = path[currentDepth];

    // Handle Enter and Tab (selection)
    if (event.key === 'Enter' || (this.props.saveMenuSelectionOnTab && event.key === 'Tab')) {
      if (!currentOption) return;
      if (!this.props.tabOnEnter) {
        event.stopPropagation();
        event.preventDefault();
      }
      // select on enter
      if (this.doOptionSelect(event, currentOption)) {
        this.closeMenus();
      }
      return;
    }

    // Handle Escape (close menu)
    if (event.key === 'Tab' || event.key === 'Escape') {
      this.closeMenus(event);
      return;
    }

    // If nothing is selected, handle initial navigation
    if (!currentOption) {
      if (event.key === 'ArrowDown') {
        this.initMenu('first');
      } else if (event.key === 'ArrowUp') {
        if (this.scrollRef()) {
          this.scrollRef().scrollTop = this.scrollRef().scrollHeight;
        }
        this.initMenu('last');
      }
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // Prevent default for all arrow keys
    if (['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Handle ArrowRight (open submenu)
    if (event.key === 'ArrowRight' && currentOption.subMenu) {
      this.openSubMenu(currentOption, true);
      return;
    }

    // Handle ArrowLeft (close submenu)
    if (event.key === 'ArrowLeft') {
      if (path.length > 1) {
        path = path.slice(0, -1);
        subNodes = subNodes.slice(0, -1);
        this.setState({ path, subNodes });
      }
      return;
    }

    // Handle ArrowDown (move to next item) - updated: do NOT auto-open submenu
    if (event.key === 'ArrowDown') {
      const nextOption = this.getNextOption(this.state.path[currentDepth]);
      const { id, menuIndex } = nextOption;

      const scroller = this.scrollRefInternal[currentDepth];
      const child = document.getElementById(id);

      if (scroller && child) {
        if (menuIndex === 0) {
          // wrapped to first
          scroller.scrollTop = 0;
        } else if (child.offsetTop + 2 * child.offsetHeight >= scroller.offsetHeight + scroller.scrollTop) {
          scroller.scrollTop += child.offsetHeight;
        }
      }

      const path = this.state.path.slice(0, -1).concat([nextOption]);
      this.setState({ path });

      return;
    }

    // Handle ArrowUp (move to previous item) - updated: do NOT auto-open submenu
    if (event.key === 'ArrowUp') {
      const prevOption = this.getPrevOption(this.state.path[currentDepth]);
      const { id, menuIndex, menuDepth } = prevOption;

      const scroller = this.scrollRefInternal[currentDepth];
      const child = document.getElementById(id);

      if (scroller && child) {
        if (menuIndex === this.trackedOptions[menuDepth].length - 1) {
          // wrapped to last
          scroller.scrollTop = scroller.scrollHeight;
        } else if (child.offsetTop - child.offsetHeight <= scroller.scrollTop) {
          scroller.scrollTop -= child.offsetHeight;
        }
      }

      const path = this.state.path.slice(0, -1).concat([prevOption]);
      this.setState({ path });
      return;
    }
  };

  findOption = (options, option, path = []) => {
    let pathFound;

    options.forEach((item) => {
      if (item.value === option.value) {
        pathFound = [...path, item];
      } else if (item.subMenu && item.subMenu.length) {
        const subResult = this.findOption(item.subMenu, option, [...path, item]);
        if (subResult) {
          pathFound = subResult;
        }
      }
    });

    return pathFound;
  };

  initMenu = (firstOrLast, offset = 0) => {
    if (!this.props.open) return;
    let path;
    if (this.props.selectedOption) {
      path = this.findOption(this.trackedOptions[0], this.props.selectedOption);
    } else if (firstOrLast === 'first') {
      const first = this.trackedOptions[0][offset];
      if (first) {
        path = [first];
        setTimeout(() => {
          const scroll = this.scrollRef();
          if (scroll && scroll.scrollTop) {
            scroll.scrollTop = 0;
          }
        }, 0);
      }
    } else if (firstOrLast === 'last') {
      const last = this.trackedOptions[0][this.trackedOptions[0].length - 1 + offset];
      path = last ? [last] : undefined;
    }
    if (!path || !path.length) return;

    const option = path[this.getCurrentDepth()];
    this.setState({
      path,
      subNodes: option && option.subMenu ? [ensureArray(option.subMenu)] : [],
    });
  };

  getCurrentDepth = () => this.state.path.length - 1;

  getOptionId = (option, depth, index) => option.id ?? `${index}${option.label}${depth}`;
  // getOptionId = (option, depth, index) => option.id ?? `${depth}_${index}_${option.label}`;

  getNextOption = (currentOption) => {
    if (currentOption.menuIndex >= this.trackedOptions[currentOption.menuDepth].length - 1) {
      return this.trackedOptions[currentOption.menuDepth][0]; // wraps back around to the beginning
    }
    return this.trackedOptions[currentOption.menuDepth][currentOption.menuIndex + 1];
  };

  getPrevOption = (currentOption) => {
    if (currentOption.menuIndex <= 0) {
      return this.trackedOptions[currentOption.menuDepth][this.trackedOptions[currentOption.menuDepth].length - 1]; // wrap around to end
    }
    return this.trackedOptions[currentOption.menuDepth][currentOption.menuIndex - 1];
  };

  pathContainsOption = (path, option) =>
    path.some(
      (crumb) =>
        crumb.label === option.label && option.menuDepth === crumb.menuDepth && crumb.menuIndex === option.menuIndex,
    );

  composeItems = (options, depth = 0) => {
    if (!options) return [];
    if (this.trackedOptions.length < depth + 1) this.trackedOptions.push([]);
    else this.trackedOptions[depth] = [];
    const items = [];
    const { path } = this.state;
    options.forEach((option, index) => {
      if (!option) {
        return;
      }
      if (option.isLabel) {
        items.push(
          <Typography
            id={this.getOptionId(option, depth, index)}
            variant='captionRegular'
            className={this.props.classes.labelStyles}
            key={`label-${option.label}`}
          >
            {option.label}
          </Typography>,
        );
      } else if (option.isSubheader) {
        items.push(
          <ListItem
            id={this.getOptionId(option, depth, index)}
            key={`subtitle-${option.label}`}
            style={{ backgroundColor: option.backgroundColor || '' }}
            className={this.props.classes.subheader}
          >
            {option.startIcon && (
              <ListItemIcon className={this.props.classes.startIcon}>{option.startIcon}</ListItemIcon>
            )}
            <Typography variant='captionRegular'>{option.label}</Typography>
            {option.endIcon && <ListItemIcon className={this.props.classes.startIcon}>{option.endIcon}</ListItemIcon>}
          </ListItem>,
        );
      } else if (option.isGroupList) {
        // TODO: add group lists into the menu
      } else if (!option.nop) {
        // regular items and ones with submenus
        const trackedOption = {
          ...option,
          menuDepth: depth,
          menuIndex: this.trackedOptions[depth].length,
          id: this.getOptionId(option, depth, index),
        };
        this.trackedOptions[depth].push(trackedOption);
        const onClick = option.disabled ? noop : (event) => this.handleOptionClick(event, trackedOption, depth);
        const itemToPush = (
          <ListItem
            id={trackedOption.id}
            // eslint-disable-next-line react/no-array-index-key
            key={`qnested-menuitem-${index}${option.label}${depth}`}
            onMouseEnter={(event) => this.handleOptionHover(event, trackedOption)}
            disabled={option.disabled}
            onClick={onClick}
            className={classNames(
              !option.disabled && this.props.classes.item,
              option.disabled && this.props.classes.disabledItem,
              this.state.interactionMode === 'keyboard' && 'keyboard-mode',
            )}
            role='menuitem'
            style={{
              backgroundColor:
                !option.disabled &&
                (!this.pathContainsOption(path, trackedOption)
                  ? option.backgroundColor || ''
                  : this.props.theme.palette.action.hover),
              ...(option.style ? option.style : {}),
            }}
            // selected={this.pathContainsOption(path, trackedOption)}
          >
            {option.customRender ? (
              option.customRender
            ) : (
              <Stack direction='row' alignItems='center'>
                {option.startIcon && (
                  <ListItemIcon
                    className={option.disabled ? this.props.classes.iconColorDisabled : this.props.classes.iconColor}
                    classes={{ root: this.props.classes.startIcon }}
                  >
                    {option.startIcon}
                  </ListItemIcon>
                )}
                <Stack>
                  <Typography variant={this.props.typographyVariant} color={option.labelColor}>
                    {option.label}
                  </Typography>
                  {option.subtext && (
                    <Typography variant='captionRegular' color='text.secondary'>
                      {option.subtext}
                    </Typography>
                  )}
                </Stack>
                {option.endIcon && (
                  <ListItemIcon
                    className={option.disabled ? this.props.classes.iconColorDisabled : this.props.classes.iconColor}
                    classes={{ root: this.props.classes.startIcon }}
                  >
                    {option.endIcon}
                  </ListItemIcon>
                )}
              </Stack>
            )}
            {option.subMenu && (
              <IconButton onClick={this.handleArrowClick} className={this.props.classes.rightArrowButton} size='large'>
                <ArrowIcon
                  className={
                    option.disabled ? this.props.classes.rightArrowStylesDisabled : this.props.classes.rightArrowStyles
                  }
                />
              </IconButton>
            )}
          </ListItem>
        );

        if (option.tooltip) {
          items.push(
            <QTip
              placement={option.tooltip.placement}
              solidTheme
              title={option.tooltip.text}
              noTippyTip
              arrow
              enableOnMobile
              key={option.tooltip.text}
            >
              {itemToPush}
            </QTip>,
          );
        } else {
          items.push(itemToPush);
        }
      }

      if (option.divider) {
        items.push(
          <Divider
            // eslint-disable-next-line react/no-array-index-key
            key={`${index}divider${depth}`}
            className={this.props.classes.divider}
          />,
        );
      }
    });
    return items;
  };

  getAnchor = () => this.props.anchorEl || null;

  closeMenus = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (this.props.onClose && this.props.open) {
      this.props.onClose();
    }
    this.clearAllTracking();
    if (this.props.currentHover) {
      this.props.currentHover(null);
    }

    // restore focus to element which opened menu
    if (this.props.anchorEl && typeof this.props.anchorEl.focus === 'function') {
      this.props.anchorEl.focus();
    }
  };

  setHeaderRef = (ref) => {
    this.headerRef = ref;
    if (ref) {
      const headerHeight = ref.getBoundingClientRect().bottom - ref.getBoundingClientRect().top;
      if (headerHeight !== this.state.headerHeight) {
        this.setState({ headerHeight });
      }
    }
  };

  setFirstMenuRef = (node) => {
    this.firstMenuRef = node;
    if (node) {
      this.scrollRefInternal[0] = node;
    }
  };

  updateFirstMenuHeight = () => {
    if (!this.getDrawerPaper() || !this.firstMenuRef) return;

    const menuHeight = this.firstMenuRef.getBoundingClientRect().height;
    const headerHeight = this.headerRef?.getBoundingClientRect().height ?? 0;
    const firstMenuHeight = menuHeight + headerHeight;

    if (firstMenuHeight > 0 && firstMenuHeight !== this.state.firstMenuHeight) {
      this.setState({ firstMenuHeight });
    }
  };

  clearAllTracking = () =>
    this.setState({
      path: [],
      subNodes: [],
      interactionMode: 'keyboard',
    });

  render() {
    const { anchorEl, options, classes, open, header, width, placement, drawerFloatingSx, drawerLayerContainer } =
      this.props;
    const { subNodes, headerHeight, drawerWidth, firstMenuHeight } = this.state;
    if (!anchorEl || !options) return null;

    const spaceBelow = window.innerHeight - anchorEl.getBoundingClientRect().top - 40; // 40 for spacing
    const spaceAbove = anchorEl.getBoundingClientRect().top - 40; // 40 for spacing
    const rootPlacement = spaceAbove > spaceBelow ? 'top' : 'bottom';
    const drawerPaper = this.getDrawerPaper();
    const measuredDrawerWidth =
      drawerPaper === this.observedDrawerPaper ? drawerWidth || drawerPaper?.clientWidth : drawerPaper?.clientWidth;
    const rootFallbackPlacements = drawerPaper
      ? [rootPlacement === 'top' ? 'bottom' : 'top']
      : ['right', 'left', 'bottom', 'top'];
    const drawerBoundaryModifiers = drawerPaper
      ? [
          {
            name: 'preventOverflow',
            options: {
              altAxis: true,
              boundary: drawerPaper,
              padding: 8,
              tether: false,
            },
          },
        ]
      : [];
    const drawerColumnCount = drawerPaper ? 1 + subNodes.filter(Boolean).length : 1;
    const drawerMenuLayout = drawerPaper
      ? getDrawerMenuLayout({
          columnCount: drawerColumnCount,
          drawerWidth: measuredDrawerWidth,
        })
      : null;

    // A floating flyout (not the drawer column layout) that lives inside a drawer
    // otherwise portals into the drawer layer, trapping it in the drawer's
    // stacking context (zIndex.drawer). The shared drawer-edge overlay
    // (zIndex.modal - 1) then paints over any part of the menu that overflows the
    // drawer bounds. Portaling to the default top layer (document.body) instead
    // lets the menu's own zIndex.modal (via drawerFloatingSx) sit above that edge
    // overlay. The column layout stays in the drawer layer so it remains clipped.
    const menuContainer = drawerLayerContainer && !drawerPaper ? undefined : drawerLayerContainer;

    // Added a max height so it doesn't go off screen for large category boxes.
    const rootMenuMaxHeight = this.props.disableMenuScrolling
      ? undefined
      : Math.max(Math.max(spaceAbove, spaceBelow) - headerHeight, 0);
    const nestedDrawerMenuMaxHeight = this.props.disableMenuScrolling
      ? undefined
      : firstMenuHeight || rootMenuMaxHeight;

    const menuItems = this.composeItems(options, 0);

    // callback to let parent know if the user is navigating through the menu
    if (this.props.currentHover) {
      if (this.state.path.length > 0 && open) {
        this.props.currentHover(this.state.path[this.getCurrentDepth()]);
      } else {
        this.props.currentHover(null);
      }
    }

    return (
      <Modal
        className={classes.root}
        container={menuContainer}
        sx={drawerFloatingSx}
        open={Boolean(open)}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            event.stopPropagation();
            this.closeMenus();
          }
        }}
        BackdropProps={{ invisible: true }}
        disableAutoFocus={!this.props.isNestedMenu}
        disableEnforceFocus={!this.props.isNestedMenu}
      >
        <div>
          <Popper
            container={menuContainer}
            id='picker-boi'
            data-testid='picker-boi'
            key='picker-boi'
            open
            anchorEl={this.getAnchor}
            onClick={(event) => {
              event?.stopPropagation();
              event?.preventDefault();
            }}
            placement={placement || rootPlacement}
            className={menuItems.length > 0 ? classNames(classes.popper, classes.rootPopper) : ''}
            sx={drawerFloatingSx}
            style={{
              maxWidth: drawerMenuLayout ? 'none' : undefined,
              minWidth: drawerMenuLayout ? 0 : width || undefined,
              width: drawerMenuLayout?.width ?? width ?? anchorEl.offsetWidth,
            }}
            modifiers={[
              {
                name: 'flip',
                options: {
                  boundary: drawerPaper || undefined,
                  fallbackPlacements: rootFallbackPlacements,
                  padding: drawerPaper ? 8 : undefined,
                },
              },
              ...drawerBoundaryModifiers,
            ]}
          >
            {drawerMenuLayout ? (
              <div
                className={classes.drawerMenuColumns}
                data-testid='drawer-nested-menu-columns'
                ref={(node) => {
                  this.drawerMenuColumns = node;
                }}
              >
                <div
                  className={classes.drawerMenuColumn}
                  data-drawer-menu-column
                  style={{ width: drawerMenuLayout.columnWidth }}
                >
                  <div key='headerDiv==' ref={this.setHeaderRef}>
                    {header}
                  </div>
                  <div
                    id='top-wrapper-qnested-menu'
                    className={classNames(classes.primaryMenu, this.props.disableMenuScrolling && classes.staticMenu)}
                    style={{ boxSizing: 'border-box', maxHeight: rootMenuMaxHeight }}
                    ref={this.setFirstMenuRef}
                  >
                    {menuItems.map((item) => item)}
                  </div>
                </div>
                {open &&
                  this.state.path.map((option, index) => {
                    if (!subNodes[index]) return null;
                    const { label } = option;
                    const popperId = `qnested-popper-menu-item-${label}`;
                    const id = `qnested-menu-item-${label}`;
                    return (
                      <div
                        className={classes.drawerMenuColumn}
                        data-drawer-menu-column
                        data-testid={popperId}
                        id={popperId}
                        key={id}
                        style={{ width: drawerMenuLayout.columnWidth }}
                      >
                        <div
                          id={id}
                          className={classNames(
                            classes.secondaryMenu,
                            this.props.disableMenuScrolling && classes.staticMenu,
                          )}
                          style={{
                            boxSizing: 'border-box',
                            maxHeight: nestedDrawerMenuMaxHeight,
                            maxWidth: 'none',
                          }}
                          ref={(node) => {
                            if (node) {
                              this.scrollRefInternal[index + 1] = node;
                            }
                          }}
                        >
                          {this.composeItems(subNodes[index], index + 1).map((item) => item)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <>
                <div key='headerDiv==' ref={this.setHeaderRef}>
                  {header}
                </div>
                <div
                  id='top-wrapper-qnested-menu'
                  className={classNames(classes.primaryMenu, this.props.disableMenuScrolling && classes.staticMenu)}
                  style={{ boxSizing: 'border-box', maxHeight: rootMenuMaxHeight }}
                  ref={this.setFirstMenuRef}
                >
                  {menuItems.map((item) => item)}
                </div>
              </>
            )}
          </Popper>
          {!drawerPaper &&
            open &&
            this.state.path.map((option, index) => {
              if (!subNodes[index]) return null;
              const { label } = option;
              const popperId = `qnested-popper-menu-item-${label}`;
              const id = `qnested-menu-item-${label}`;
              return (
                <Popper
                  container={menuContainer}
                  id={popperId}
                  key={popperId}
                  data-testid={popperId}
                  open
                  anchorEl={document.getElementById(option.id)}
                  className={classes.popper}
                  sx={drawerFloatingSx}
                  placement='right'
                >
                  <div
                    id={id}
                    className={classNames(classes.secondaryMenu, this.props.disableMenuScrolling && classes.staticMenu)}
                    ref={(node) => {
                      if (node) {
                        // +1 to accommodate for root-menu
                        this.scrollRefInternal[index + 1] = node;
                      }
                    }}
                  >
                    {this.composeItems(subNodes[index], index + 1).map((item) => item)}
                  </div>
                </Popper>
              );
            })}
        </div>
      </Modal>
    );
  }
}

NestedQMenu.propTypes = {
  onClose: PropTypes.func,
  anchorEl: PropTypes.object,
  classes: PropTypes.object,
  onChange: PropTypes.func,
  options: PropTypes.array,
  selectedOption: PropTypes.object,
  open: PropTypes.bool,
  header: PropTypes.object,
  theme: PropTypes.object,
  currentHover: PropTypes.func, // callback that passes the option currently being hovered, has some extra key-values, just ignore
  width: PropTypes.number,
  tabOnEnter: PropTypes.bool,
  placement: PropTypes.string,
  saveMenuSelectionOnTab: PropTypes.bool,
  typographyVariant: PropTypes.string,
  isNestedMenu: PropTypes.bool,
  disableMenuScrolling: PropTypes.bool,
  drawerFloatingSx: PropTypes.object,
  drawerLayerContainer: PropTypes.object,
  useDrawerColumnLayout: PropTypes.bool,
};

const StyledNestedQMenu = compose(withTheme)(withStyles(NestedQMenu, styles));

function NestedQMenuWithDrawerLayer(props) {
  const drawerFloatingSx = getStandardDrawerFloatingSx();
  const drawerLayerContainer = useStandardDrawerLayerContainer();
  return (
    <StyledNestedQMenu {...props} drawerFloatingSx={drawerFloatingSx} drawerLayerContainer={drawerLayerContainer} />
  );
}

export default NestedQMenuWithDrawerLayer;
